import { z } from "zod"
import { channelResponseSchema } from "../campaigns/commonTypes"
import { CapabilityExecutionStatus, CapabilityKind } from "../capabilities/dtos"
import { CapabilityReasonSchema } from "../capabilities/reasons"
import {
  CompanyClientResponseDataSchema,
  CustomerLogResponseDataSchema,
} from "../customers/response_data"
import { GoalResponseDataSchema, GoalTypeEnum } from "../goals/response_data"
import { WhatsappTemplateResponseDataSchema } from "../integrations"
import { MessageProductAttachmentsSchema } from "../messages/attachments"
import { ConversationNoteResponseDataSchema } from "../notes/response_data"
import { PrismaJsonSchema } from "../shared/utils"

export const ConversationTestingExperimentOptionResponseDataSchema = z.object({
  domain: z.string().min(1),
  name: z.string().min(1),
})

export const ConversationTestingExperimentOptionsResponseDataSchema = z.array(
  ConversationTestingExperimentOptionResponseDataSchema,
)

export const ConversationStatusEnum = {
  Pending: "Pending",
  Failed: "Failed",
  Reached: "Reached",
  Opened: "Opened",
  Engaged: "Engaged",
  Achieved: "Achieved",
} as const

export type ConversationStatus =
  (typeof ConversationStatusEnum)[keyof typeof ConversationStatusEnum]

export const ConversationFailureTypeEnum = {
  CouldNotStart: "CouldNotStart",
  MessageBounced: "MessageBounced",
} as const

export type ConversationFailureType =
  (typeof ConversationFailureTypeEnum)[keyof typeof ConversationFailureTypeEnum]

export const ConversationTypeEnum = {
  Live: "Live",
  Testing: "Testing",
} as const

export const MessageRoleEnum = {
  User: "User",
  Assistant: "Assistant",
} as const

export const MessageKindEnum = {
  Generic: "Generic",
  Campaign: "Campaign",
  Reengage: "Reengage",
  Question: "Question",
  Action: "Action",
  Closing: "Closing",
  OptOut: "OptOut",
  Employee: "Employee",
  Resolver: "Resolver",
  FindOrder: "FindOrder",
  CancelOrder: "CancelOrder",
  CheckReturn: "CheckReturn",
} as const

export const MessageFormatEnum = {
  Text: "Text",
  Audio: "Audio",
  Image: "Image",
  Video: "Video",
  Document: "Document",
  Unsupported: "Unsupported",
} as const

export const TicketStatusEnum = {
  Pending: "Pending",
  PartiallyResolved: "PartiallyResolved",
  Resolved: "Resolved",
  Closed: "Closed",
} as const

export const ConversationFlowEnum = {
  Inbound: "Inbound",
  Outbound: "Outbound",
} as const

export const OpportunityQualificationEnum = {
  Detected: "Detected",
  Potential: "Potential",
  Qualified: "Qualified",
  Disqualified: "Disqualified",
} as const

export const ConversationOpportunityResponseDataSchema = z.object({
  id: z.bigint().transform(String),
  conversationId: z.bigint().transform(String),
  qualification: z.enum(OpportunityQualificationEnum),
  summary: z.string().nullable(),
  createdAt: z.date().transform((value) => value.toISOString()),
  escalatedAt: z
    .date()
    .transform((value) => value.toISOString())
    .optional()
    .nullable(),
  campaign: z
    .object({
      id: z.bigint().transform(String),
      name: z.string().nullable(),
    })
    .optional()
    .nullable(),
})

export const ConversationOpportunitiesResponseDataSchema = z.array(
  ConversationOpportunityResponseDataSchema,
)

export const ConversationTimelineTicketResponseDataSchema = z.object({
  id: z.bigint().transform(String),
  status: z.enum(TicketStatusEnum),
  createdAt: z.date().transform((value) => value.toISOString()),
  updatedAt: z.date().transform((value) => value.toISOString()),
})

export const ConversationTimelineQuestionResponseDataSchema = z.object({
  id: z.bigint().transform(String),
  subject: z.string().optional().nullable(),
  summary: z.string(),
  resolvedAutomaticallyAt: z
    .date()
    .transform((value) => value.toISOString())
    .optional()
    .nullable(),
  conversationTicketId: z.bigint().transform(String).optional().nullable(),
  createdAt: z.date().transform((value) => value.toISOString()),
  updatedAt: z.date().transform((value) => value.toISOString()),
})

export const ConversationCapabilityExecutionResponseDataSchema = z.object({
  id: z.bigint().transform(String),
  capabilityId: z.bigint().transform(String),
  capabilityIdentifier: z.string(),
  capabilityName: z.string(),
  result: z.any().nullable(),
  reason: CapabilityReasonSchema.nullable(),
  evaluation: z.any().nullable(),
  raw: z.any().nullable().optional(),
  status: z.enum(CapabilityExecutionStatus),
  capabilityKind: z.enum(CapabilityKind),
  cacheExecutionId: z.bigint().transform(String).nullable(),
  isVisibleInConversation: z.boolean(),
  createdAt: z.date().transform((value) => value.toISOString()),
})

export const ConversationTicketIntegrationKindEnum = {
  Gorgias: "Gorgias",
  FrontApp: "FrontApp",
  Zendesk: "Zendesk",
  Amphora: "Amphora",
} as const

export const MessageResponseDataSchema = z.object({
  id: z.bigint().transform(String),
  role: z.enum(MessageRoleEnum),
  kind: z.enum(MessageKindEnum).optional().nullable(),
  content: z.string().nullable(),
  mediaUrl: z.string().nullable(),
  transcription: z.string().optional().nullable(),
  format: z.enum(MessageFormatEnum),
  createdAt: z.date().transform((value) => value.toISOString()),
  externalDeletedAt: z
    .date()
    .transform((value) => value.toISOString())
    .optional()
    .nullable(),
  conversationId: z.bigint().transform(String),
  employeeId: z.bigint().transform(String).optional().nullable(),
  employee: z
    .object({
      id: z.bigint().transform(String),
      name: z.string(),
      email: z.string(),
    })
    .optional()
    .nullable(),
  readAt: z
    .date()
    .transform((value) => value.toISOString())
    .optional()
    .nullable(),
  messageReactions: z.array(z.object({ emoji: z.string() }).optional()).optional(),
  whatsappTemplate: WhatsappTemplateResponseDataSchema.nullish(),
  whatsappTemplateId: z.bigint().transform(String).nullish(),
  isOneTimeTemplate: z.boolean().optional(),
  whatsappTemplateButtons: z
    .array(z.object({ text: z.string(), url: z.string().nullable() }))
    .optional(),
  sentAt: z
    .date()
    .transform((value) => value.toISOString())
    .nullish(),
  responseToMessageId: z.bigint().transform(String).nullish(),
  failedAt: z
    .date()
    .transform((value) => value.toISOString())
    .optional()
    .nullable(),
  failedReason: z.string().nullish(),
  source: z.string().nullish(),
  attachments: z.preprocess((v) => v, MessageProductAttachmentsSchema.nullish()),
  isGenerative: z.boolean().nullish(),
})

export const EnhancedMessageResponseDataSchema = z.object({
  enhancedMessage: z.string().nullable(),
})

export const ConversationGoalResponseDataSchema = z.object({
  goal: GoalResponseDataSchema,
  shots: z.number(),
  order: z.number(),
  answeredAt: z
    .date()
    .transform((value) => (value ? value.toISOString() : null))
    .optional()
    .nullable(),
  achievedAt: z
    .date()
    .transform((value) => (value ? value.toISOString() : null))
    .optional()
    .nullable(),
})

export const ConversationGoalsResponseDataSchema = z.array(ConversationGoalResponseDataSchema)

export const ConversationFlagResponseDataSchema = z.object({
  id: z.bigint().transform(String),
  messageId: z
    .bigint()
    .nullable()
    .transform((value) => value?.toString() ?? null),
  kind: z.enum(["Friction", "Issue"]),
  key: z.string(),
})

export const ConversationResponseDataSchema = z.object({
  id: z.bigint().transform(String),
  aiDisabled: z.boolean(),
  aiDisabledPermanently: z.boolean(),
  aiDisabledUntil: z
    .date()
    .nullable()
    .transform((value) => value?.toISOString() ?? null),
  status: z.enum(ConversationStatusEnum),
  type: z.enum(ConversationTypeEnum),
  hasMessages: z.boolean(),
  whatsappRoute: z
    .object({ senderNumber: z.string(), recipientCallingCode: z.string().nullable() })
    .nullish(),
  orderId: z.string().nullable().optional(),
  overallSentiment: z.number().nullable().optional(),
  customer: z.object({
    id: z.bigint().transform(String),
    name: z.string().optional().nullable(),
    firstName: z.string().nullish(),
    lastName: z.string().nullish(),
    tentativeName: z.string().optional().nullable(),
    tentativeFirstName: z.string().nullish(),
    tentativeLastName: z.string().nullish(),
    isFake: z.boolean(),
    email: z.string().nullish(),
    tentativeEmail: z.string().nullish(),
    instagramProfile: z
      .object({
        name: z.string().nullable(),
        username: z.string().nullable(),
      })
      .nullish(),
    locale: z.string().nullish(),
    preferredLocale: z.string().nullish(),
    denylistedAt: z
      .date()
      .optional()
      .nullable()
      .transform((value) => value?.toISOString()),
    optedOutAt: z
      .date()
      .optional()
      .nullable()
      .transform((value) => value?.toISOString()),
    lastMessageSentAt: z
      .date()
      .transform((value) => value.toISOString())
      .nullish(),
    client: CompanyClientResponseDataSchema.nullish(),
  }),
  customerLogs: z.array(CustomerLogResponseDataSchema).optional(),
  campaign: z
    .object({
      id: z.bigint().transform(String),
      name: z.string().nullable(),
      channel: channelResponseSchema,
    })
    .optional()
    .nullable(),
  goals: ConversationGoalsResponseDataSchema.optional(),
  channel: channelResponseSchema.optional(),
  createdAt: z.date().transform((value) => value.toISOString()),
  updatedAt: z.date().transform((value) => value.toISOString()),
  closedAt: z
    .date()
    .transform((value) => value.toISOString())
    .optional()
    .nullable(),
  company: z
    .object({
      id: z.bigint().transform(String),
      name: z.string().optional().nullable(),
      description: z.string().optional().nullable(),
      logo: z.string().optional().nullable(),
      webChatConfig: z.any(),
    })
    .optional(),
  conversationTickets: z.array(ConversationTimelineTicketResponseDataSchema).optional(),
  hasTickets: z.boolean().optional(),
  hasIssueUpdates: z.boolean().optional(),
  hasNewIssues: z.boolean().optional(),
  conversationQuestions: z.array(ConversationTimelineQuestionResponseDataSchema).optional(),
  conversationOpportunities: ConversationOpportunitiesResponseDataSchema.optional(),
  conversationCapabilityExecutions: z
    .array(ConversationCapabilityExecutionResponseDataSchema)
    .optional(),
  conversationFlags: z.array(ConversationFlagResponseDataSchema).optional(),
  notes: z.array(ConversationNoteResponseDataSchema).optional(),
  event: PrismaJsonSchema.optional().nullable(),
  conversationFlow: z.enum(ConversationFlowEnum).optional().nullable(),
  summary: z.string().optional().nullable(),
  failedReason: z.string().optional().nullable(),
  failedAt: z
    .date()
    .transform((value) => value.toISOString())
    .optional()
    .nullable(),
  conversationExperiments: z
    .array(
      z.object({
        experiment: z.string(),
        domain: z.string(),
      }),
    )
    .nullish(),
})

// The web widget is the only consumer, and it renders nothing but the message list.
const PublicMessageResponseDataSchema = MessageResponseDataSchema.pick({
  id: true,
  role: true,
  content: true,
  mediaUrl: true,
  format: true,
  createdAt: true,
  messageReactions: true,
  attachments: true,
})

export const PublicConversationResponseDataSchema = z.object({
  hasOlderMessages: z.boolean(),
  messages: z.array(PublicMessageResponseDataSchema),
})

export const ConversationTimelineResponseDataSchema = z.object({
  messages: z.array(MessageResponseDataSchema),
  conversationTickets: z.array(ConversationTimelineTicketResponseDataSchema),
  conversationQuestions: z.array(ConversationTimelineQuestionResponseDataSchema),
  conversationOpportunities: ConversationOpportunitiesResponseDataSchema,
  conversationCapabilityExecutions: z.array(ConversationCapabilityExecutionResponseDataSchema),
  customerLogs: z.array(CustomerLogResponseDataSchema),
  pageInfo: z.object({
    hasMore: z.boolean(),
    oldestMessageId: z.bigint().transform(String).nullable(),
    newestMessageId: z.bigint().transform(String).nullable(),
  }),
})

export const ConversationAmphoraOriginResponseDataSchema = z
  .object({
    company: z.object({
      id: z.bigint().transform(String),
      name: z.string(),
    }),
    conversations: z.array(
      z.object({
        id: z.bigint().transform(String),
        customerId: z.bigint().transform(String),
        createdAt: z.date().transform((value) => value.toISOString()),
        timeline: ConversationTimelineResponseDataSchema,
      }),
    ),
  })
  .nullable()

export const PromptEvaluationConversationSnapshotMessageSchema = z.object({
  sourceId: z.bigint().transform(String),
  role: z.enum(MessageRoleEnum),
  kind: z.enum(MessageKindEnum).optional().nullable(),
  content: z.string().nullable(),
  createdAt: z.date().transform((value) => value.toISOString()),
  negativeRatings: z.number(),
})

export const PromptEvaluationConversationSnapshotMetadataResponseDataSchema = z.object({
  conversationId: z.bigint().transform(String),
  recommendedLastMessageId: z.bigint().transform(String).nullable(),
  messages: z.array(PromptEvaluationConversationSnapshotMessageSchema),
})

export const PromptEvaluationConversationSnapshotResponseDataSchema = z.object({
  version: z.literal(1),
  exportedAt: z.string(),
  source: z.object({
    conversationId: z.string(),
    customerId: z.string(),
    companyId: z.string(),
    campaignId: z.string().nullable(),
    pineconeCompanyId: z.string().optional(),
    lastMessageId: z.string(),
    lastPresentMessageId: z.string().optional(),
  }),
  knowledgeBase: z
    .object({
      mode: z.enum(["live-source", "local", "snapshot"]).default("live-source"),
      pineconeCompanyId: z.string().optional(),
      fallback: z.enum(["none", "snapshot"]).default("snapshot"),
      snapshotChunks: z
        .array(
          z.object({
            sourceId: z.string().optional(),
            score: z.number(),
            content: z.string(),
          }),
        )
        .default([]),
    })
    .optional(),
  scenario: z.object({
    name: z.string(),
    companyId: z.string(),
    campaignId: z.string().optional(),
    pineconeCompanyId: z.string().optional(),
    agentContext: z.string().optional(),
    maxTurns: z.number(),
    promptEvaluationSetup: z
      .object({
        integrations: z
          .object({
            shopify: z.boolean().default(false),
            reveni: z.boolean().default(false),
          })
          .default({ shopify: false, reveni: false }),
        capabilities: z
          .array(
            z.object({
              identifier: z.string(),
              config: z.record(z.string(), z.unknown()).default({}),
            }),
          )
          .default([]),
      })
      .optional(),
    goals: z.array(
      z.object({
        id: z.string(),
        userInstructions: z.string(),
        capabilityTriggers: z.array(z.string()),
      }),
    ),
    requiredCapabilityCalls: z.array(z.string()),
  }),
  persona: z.object({
    id: z.string(),
    name: z.string(),
    description: z.string(),
    avgMessages: z.number(),
    instructions: z.array(z.string()),
  }),
  customer: z.object({
    name: z.string().nullable().optional(),
    email: z.string().nullable().optional(),
    phone: z.string().nullable().optional(),
    locale: z.string().nullable().optional(),
    preferredLocale: z.string().nullable().optional(),
    metadata: z.unknown().optional(),
  }),
  conversation: z.object({
    channel: z.string(),
    conversationFlow: z.string().nullable().optional(),
    event: z.unknown().nullable().optional(),
    summary: z.string().nullable().optional(),
  }),
  sourceConfiguration: z
    .object({
      companyProfile: z
        .object({
          description: z.string().nullable(),
          products: z.string().nullable(),
          toneOfVoice: z.string().nullable(),
          additionalInfo: z.string().nullable(),
        })
        .nullable()
        .optional(),
      campaign: z
        .object({
          id: z.string(),
          name: z.string().nullable(),
          channel: z.string().nullable(),
          flow: z.string().nullable(),
          toneOfVoice: z.string().nullable(),
        })
        .nullable(),
      capabilities: z.array(
        z.object({
          identifier: z.string(),
          kind: z.string(),
          campaignAssignation: z.string(),
          campaignFlow: z.string().nullable(),
          config: z.unknown().nullable(),
          isActiveInSourceCampaign: z.boolean(),
        }),
      ),
    })
    .optional(),
  messages: z.array(
    z.object({
      sourceId: z.string(),
      role: z.enum(["user", "assistant"]),
      kind: z.enum(MessageKindEnum).optional().nullable(),
      content: z.string(),
      createdAt: z.string(),
      negativeRatings: z.number(),
    }),
  ),
  evidence: z.object({
    capabilityExecutions: z.array(
      z.object({
        sourceId: z.string(),
        identifier: z.string(),
        status: z.string(),
        reason: z.string().nullable(),
        evaluation: z.unknown().nullable(),
        result: z.unknown().nullable(),
      }),
    ),
    tickets: z.array(
      z.object({
        sourceId: z.string(),
        status: z.string(),
        questionSourceIds: z.array(z.string()),
      }),
    ),
    questions: z.array(
      z.object({
        sourceId: z.string(),
        summary: z.string(),
        subject: z.string().nullable().optional(),
        conversationTicketSourceId: z.string().nullable(),
        resolvedAutomaticallyAt: z.string().nullable(),
      }),
    ),
    opportunities: z.array(
      z.object({
        sourceId: z.string(),
        qualification: z.string(),
        summary: z.string().nullable(),
        escalatedAt: z.string().nullable(),
      }),
    ),
    knowledgeBaseItems: z.array(
      z.object({
        messageSourceId: z.string(),
        changeLogId: z.string().nullable(),
        score: z.number(),
      }),
    ),
  }),
})

export const ConversationTestingResponseDataSchema = z.union([
  ConversationResponseDataSchema,
  z.object({
    filterOutReasons: z.array(z.string()).nullish(),
  }),
])

export const ConversationsResponseDataSchema = z.array(ConversationResponseDataSchema)

export const ConversationCustomerResponseDataSchema = z.object({
  id: z.bigint().transform(String),
  name: z.string().optional().nullable(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  email: z.string().optional().nullable(),
  phone: z.string().optional().nullable(),
  metadata: z
    .union([z.record(z.string(), z.any()), z.number(), z.boolean(), z.string()])
    .optional()
    .nullable(),
})

export const ConversationResponseResponseDataSchema = z.object({
  id: z.bigint().transform(String),
  conversationId: z.bigint().transform(String),
  customerId: z.bigint().transform(String),
  customer: ConversationCustomerResponseDataSchema,
  goalId: z.bigint().transform(String),
  goalName: z.string(),
  goalDisplayName: z.string(),
  goalType: z.enum(GoalTypeEnum),
  goalOptionId: z.bigint().transform(String).optional().nullable(),
  goalOptionValue: z.string().optional().nullable(),
  response: z.string().nullable(),
  subject: z.string().nullable(),
  sentiment: z.number().nullable(),
  createdAt: z.date().transform((value) => value.toISOString()),
})

export const ConversationResponsesResponseDataSchema = z.array(
  ConversationResponseResponseDataSchema,
)

export const GetOngoingMessagesResponseDataSchema = z.object({
  messages: z.array(MessageResponseDataSchema),
})

export const ConversationsStatusesResponseDataSchema = z.object({
  [ConversationStatusEnum.Pending]: z.number().optional().nullable(),
  [ConversationStatusEnum.Failed]: z.number().optional().nullable(),
  [ConversationStatusEnum.Reached]: z.number().optional().nullable(),
  [ConversationStatusEnum.Opened]: z.number().optional().nullable(),
  [ConversationStatusEnum.Engaged]: z.number().optional().nullable(),
  [ConversationStatusEnum.Achieved]: z.number().optional().nullable(),
  ["total"]: z.number().optional(),
})

export const ConversationsMessagesResponseDataSchema = z.object({
  total: z.number().optional(),
  daily: z.array(
    z.object({
      date: z.string(),
      [MessageRoleEnum.Assistant]: z.number().optional(),
      [MessageRoleEnum.User]: z.number().optional(),
    }),
  ),
})
